import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { NewTabLink } from "components/NewTabLink";
import { PhoneNumberField } from "components/PhoneNumberField";

import {
  TERMS_OF_SERVICE_URL,
  WORK_PHONE_EXTENSION_MAX_LENGTH,
} from "../../../../constants";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { PasswordToggableIconEndAdornment } from "../PasswordToggableIconEndAdornment";
import { SIGNUP_PERSONA_OPTIONS } from "./types";

import type {
  SignUpData,
  SignupPersona,
  SignupPhoneErrors,
  SignupPhoneField,
} from "./types";
import type { EmailAndPasswordFormErrorState } from "../../types";

export interface Props {
  persona: SignupPersona | undefined;
  showPassword: boolean;
  workPhoneNumber: string;
  workPhoneNumberExtension: string;
  cellPhoneNumber: string;
  onFormChange: (key: keyof SignUpData, value: string) => void;
  onShowPasswordChange: () => void;
  onSignup: VoidFunction;
  onTermsChange: (newValue: boolean) => void;
  onPhoneFieldBlur: (field: SignupPhoneField) => void;
  formError: EmailAndPasswordFormErrorState;
  phoneErrors: SignupPhoneErrors;
  signupDisabled: boolean;
  signupError: string | null;
  loading: boolean;
}

export function SignUp(props: Props) {
  const {
    onFormChange,
    onShowPasswordChange,
    onSignup,
    onTermsChange,
    onPhoneFieldBlur,
    persona,
    showPassword,
    workPhoneNumber,
    workPhoneNumberExtension,
    cellPhoneNumber,
    formError,
    phoneErrors,
    signupDisabled,
    signupError,
    loading,
  } = props;
  const navigate = useNavigate();
  const isMobileXS = useIsMobile({ maxWidth: 420 });

  return (
    <Box>
      <Typography textAlign="center" variant="h4" fontWeight="bold">
        Sign Up for Evotrux
      </Typography>
      <Stack my="2rem" spacing={2}>
        <TextField
          id="email-input"
          name="email"
          autoComplete="email"
          label="Email"
          variant="outlined"
          onChange={(e) => onFormChange("email", e.target.value)}
          error={formError.email != null}
          helperText={formError.email ?? ""}
          autoFocus
        />
        <TextField
          id="confirm-email-input"
          name="confirmEmail"
          autoComplete="email"
          label="Confirm Email"
          variant="outlined"
          onChange={(e) => onFormChange("confirmEmail", e.target.value)}
        />
        <TextField
          id="password-input"
          name="password"
          autoComplete="new-password"
          label="Password"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          onChange={(e) => onFormChange("password", e.target.value)}
          error={formError.password != null}
          helperText={formError.password ?? ""}
          InputProps={{
            endAdornment: (
              <PasswordToggableIconEndAdornment
                showPassword={showPassword}
                onIconClick={onShowPasswordChange}
              />
            ),
          }}
        />
        <TextField
          id="first-name-input"
          name="given-name"
          autoComplete="given-name"
          label="First name"
          variant="outlined"
          onChange={(e) => onFormChange("firstName", e.target.value)}
        />
        <TextField
          id="last-name-input"
          name="family-name"
          autoComplete="family-name"
          label="Family name"
          variant="outlined"
          onChange={(e) => onFormChange("lastName", e.target.value)}
        />
        <PhoneNumberField
          label="Work Phone Number"
          value={workPhoneNumber}
          onChange={(e) => onFormChange("workPhoneNumber", e)}
          onBlur={() => onPhoneFieldBlur("workPhoneNumber")}
          error={phoneErrors.workPhoneNumber != null}
          helperText={phoneErrors.workPhoneNumber ?? ""}
          autoComplete="tel"
          fullWidth
        />
        <TextField
          id="work-phone-extension-input"
          name="work-phone-extension"
          autoComplete="tel-extension"
          label="Extension (optional)"
          variant="outlined"
          value={workPhoneNumberExtension}
          onChange={(e) =>
            onFormChange("workPhoneNumberExtension", e.target.value)
          }
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: WORK_PHONE_EXTENSION_MAX_LENGTH,
          }}
        />
        <PhoneNumberField
          label="Cell Phone Number (optional)"
          value={cellPhoneNumber}
          onChange={(e) => onFormChange("cellPhoneNumber", e)}
          onBlur={() => onPhoneFieldBlur("cellPhoneNumber")}
          error={phoneErrors.cellPhoneNumber != null}
          helperText={phoneErrors.cellPhoneNumber ?? ""}
          autoComplete="mobile tel"
          fullWidth
        />
        <Stack
          direction={isMobileXS ? "column" : "row"}
          alignItems="center"
          spacing={2}
        >
          <Typography alignSelf="center">I am a</Typography>
          <ButtonGroup orientation={isMobileXS ? "vertical" : "horizontal"}>
            {SIGNUP_PERSONA_OPTIONS.map((option) => (
              <Tooltip key={option.value} title={option.tooltip} describeChild>
                <Button
                  variant={persona === option.value ? "contained" : "outlined"}
                  onClick={() => onFormChange("persona", option.value)}
                >
                  {option.label}
                </Button>
              </Tooltip>
            ))}
          </ButtonGroup>
        </Stack>
        <FormControl required>
          <FormControlLabel
            control={
              <Checkbox onChange={(e) => onTermsChange(e.target.checked)} />
            }
            label={
              <>
                <span>I agree to the </span>
                <NewTabLink href={TERMS_OF_SERVICE_URL}>
                  <p>{" terms of service."}</p>
                </NewTabLink>
              </>
            }
          />
        </FormControl>
        {signupError != null && (
          <Typography color="red" align="center">
            {signupError}
          </Typography>
        )}
        <LoadingWrappedNode loading={loading}>
          <Stack direction="column">
            <Button
              disabled={signupDisabled}
              variant="contained"
              onClick={onSignup}
            >
              Sign Up
            </Button>
          </Stack>
        </LoadingWrappedNode>

        <Divider variant="middle" />
        <Button onClick={() => navigate("/login")}>Have an account?</Button>
      </Stack>
    </Box>
  );
}
